import { useState, useEffect, useCallback } from "react";
import { Search, MapPin, Star, CheckCircle, Users, RefreshCw, MessageSquare, X, Send } from "lucide-react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { key: "", label: "All" },
  { key: "lender", label: "Lenders", color: "#0A84FF" },
  { key: "realtor", label: "Realtors", color: "#00FF88" },
  { key: "contractor", label: "Contractors", color: "#FF9F0A" },
  { key: "title", label: "Title Companies", color: "#AF52DE" },
];

function IntroModal({ pro, onClose }) {
  const [message, setMessage] = useState("");
  const [sending, setSending] = useState(false);

  const send = async () => {
    setSending(true);
    try {
      const res = await fetch(`${API}/api/network/intro-request`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pro_id: pro.id,
          pro_name: pro.name,
          pro_role: pro.role,
          pro_email: pro.email,
          message,
        }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success(data.message || "Introduction request sent!");
        onClose();
      } else {
        toast.error(data.detail || "Failed to send");
      }
    } catch {
      toast.error("Failed to send introduction request");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="bg-[#111] border border-white/[0.1] rounded-2xl p-6 w-full max-w-md space-y-4">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-white font-bold">Request Introduction</h3>
            <p className="text-white/40 text-sm mt-0.5">to {pro.name} · {pro.role}</p>
          </div>
          <button onClick={onClose} className="text-white/30 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-2">
          <label className="text-[10px] text-white/30 uppercase tracking-wider">Your message (optional)</label>
          <textarea
            value={message}
            onChange={e => setMessage(e.target.value)}
            placeholder={`Hi ${pro.name.split(" ")[0]}, I'd love to connect about a deal opportunity…`}
            rows={4}
            className="w-full bg-black/40 border border-white/[0.08] rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/20 resize-none focus:outline-none focus:border-[#D4AF37]/40"
            data-testid="intro-message-input"
          />
        </div>

        <div className="flex gap-3">
          <button onClick={onClose} className="flex-1 px-4 py-2.5 border border-white/[0.08] text-white/50 hover:text-white text-sm rounded-xl transition-all">
            Cancel
          </button>
          <button
            onClick={send}
            disabled={sending}
            data-testid="send-intro-btn"
            className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-[#D4AF37] hover:bg-[#D4AF37]/90 text-black font-semibold text-sm rounded-xl transition-all disabled:opacity-50"
          >
            {sending ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send Intro Request
          </button>
        </div>
      </div>
    </div>
  );
}

function BadgeChip({ badge }) {
  const icons = {
    shield: "🛡️",
    crown:  "👑",
    star:   "⭐",
    check:  "✓",
    reviews: "#",
  };
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 rounded-full font-bold border"
      style={{ color: badge.color, borderColor: `${badge.color}40`, background: `${badge.color}12` }}
      title={badge.label}
    >
      <span className="text-[8px]">{icons[badge.icon] || "●"}</span>
      {badge.label}
    </span>
  );
}

function ProCard({ pro, onIntroRequest }) {
  return (
    <div className="bg-[#0D0D0D] border border-white/[0.06] rounded-xl p-4 hover:border-white/[0.12] transition-all group">
      <div className="flex items-start gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 text-sm font-bold"
          style={{ background: `${pro.color}15`, color: pro.color }}
        >
          {pro.name?.charAt(0) || "?"}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-white font-semibold text-sm truncate">{pro.name}</p>
            {pro.verified && (
              <CheckCircle className="w-3.5 h-3.5 flex-shrink-0" style={{ color: pro.color }} />
            )}
          </div>
          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
            <span
              className="text-[10px] px-2 py-0.5 rounded-full font-semibold"
              style={{ color: pro.color, background: `${pro.color}15` }}
            >
              {pro.role}
            </span>
            {pro.company && pro.company !== pro.name && (
              <span className="text-[10px] text-white/30 truncate">{pro.company}</span>
            )}
          </div>
          {/* Verified Closer + other badges */}
          {pro.badges?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1.5">
              {pro.badges.map(b => <BadgeChip key={b.id} badge={b} />)}
            </div>
          )}
        </div>
        {pro.rating && (
          <div className="flex items-center gap-1 flex-shrink-0">
            <Star className="w-3 h-3 text-[#D4AF37] fill-[#D4AF37]" />
            <span className="text-xs text-white/60">{Number(pro.rating).toFixed(1)}</span>
            {pro.review_count > 0 && (
              <span className="text-[9px] text-white/20">({pro.review_count})</span>
            )}
          </div>
        )}
      </div>

      {(pro.city || pro.state) && (
        <div className="flex items-center gap-1 mt-3 text-[11px] text-white/30">
          <MapPin className="w-3 h-3" />
          {[pro.city, pro.state].filter(Boolean).join(", ")}
          {pro.deals_count > 0 && (
            <span className="ml-auto text-[10px] text-white/20">
              {pro.deals_count} deal{pro.deals_count !== 1 ? "s" : ""} closed
            </span>
          )}
        </div>
      )}

      {pro.specialties?.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {pro.specialties.slice(0, 3).map((s, i) => (
            <span key={i} className="text-[10px] px-2 py-0.5 bg-white/[0.04] border border-white/[0.06] rounded text-white/40">
              {s}
            </span>
          ))}
        </div>
      )}

      <div className="flex items-center justify-between mt-3 pt-3 border-t border-white/[0.05]">
        {pro.deals_count > 0 ? (
          <p className="text-[10px] text-white/20">{pro.deals_count} deals completed</p>
        ) : <span />}
        <button
          onClick={() => onIntroRequest(pro)}
          data-testid={`intro-btn-${pro.id}`}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37]/10 hover:bg-[#D4AF37]/20 border border-[#D4AF37]/20 text-[#D4AF37] text-[11px] font-semibold rounded-lg transition-all"
        >
          <MessageSquare className="w-3 h-3" /> Request Intro
        </button>
      </div>
    </div>
  );
}

export function DirectoryTab() {
  const [pros, setPros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState("");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [introTarget, setIntroTarget] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  const fetchDirectory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (role) params.set("role", role);
      if (debouncedSearch) params.set("search", debouncedSearch);
      const res = await fetch(`${API}/api/network/directory?${params}`, { credentials: "include" });
      const data = await res.json();
      setPros(data.directory || []);
    } catch (err) {
      console.error("Directory fetch failed", err);
    } finally {
      setLoading(false);
    }
  }, [role, debouncedSearch]);

  useEffect(() => { fetchDirectory(); }, [fetchDirectory]);

  return (
    <>
      {introTarget && <IntroModal pro={introTarget} onClose={() => setIntroTarget(null)} />}
      <div className="space-y-5">
        {/* Search + filter bar */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="text"
              placeholder="Search by name, city, specialty..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full bg-[#0D0D0D] border border-white/[0.08] rounded-xl pl-9 pr-4 py-2.5 text-sm text-white placeholder:text-white/20 focus:outline-none focus:border-[#D4AF37]/40"
              data-testid="directory-search"
            />
          </div>
          <div className="flex gap-1.5 overflow-x-auto scrollbar-none pb-0.5">
            {ROLES.map(r => (
              <button
                key={r.key}
                onClick={() => setRole(r.key)}
                className={`whitespace-nowrap flex-shrink-0 px-3 py-2 rounded-lg text-xs font-medium transition-all ${
                  role === r.key
                    ? "bg-[#D4AF37] text-black"
                    : "bg-white/[0.04] border border-white/[0.06] text-white/50 hover:text-white"
                }`}
                data-testid={`dir-filter-${r.key || "all"}`}
              >
                {r.label}
              </button>
            ))}
          </div>
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-16">
            <RefreshCw className="w-5 h-5 text-[#D4AF37] animate-spin" />
          </div>
        ) : pros.length === 0 ? (
          <div className="text-center py-16 space-y-3">
            <Users className="w-12 h-12 text-white/10 mx-auto" />
            <p className="text-white/40 text-sm">No professionals found</p>
            <p className="text-white/20 text-xs">Try a different search or filter</p>
          </div>
        ) : (
          <>
            <p className="text-white/30 text-xs">{pros.length} professional{pros.length !== 1 ? "s" : ""} found</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {pros.map(pro => <ProCard key={pro.id} pro={pro} onIntroRequest={setIntroTarget} />)}
            </div>
          </>
        )}
      </div>
    </>
  );
}

export default DirectoryTab;
